import { useState, useEffect, createContext, useContext } from 'react';
import { useCachedFetch } from './hooks/useCachedFetch';
import {
  BrowserRouter, Routes, Route, useNavigate, useParams,
  useLocation, Navigate
} from 'react-router-dom';
import { LangProvider, useLang } from './context/LangContext';
import {
  RESEARCHERS_API, CATEGORIES_API, LECTURES_API,
  FATWA_API, ARTICLE_API, RESEARCH_API, cachedFetch, sortScholars
} from './utils/api';
import { prefetch } from './utils/cache';
import './iiis-light.css';

// Layout
import Topbar from './components/Topbar';
import SiteHeader from './components/SiteHeader';
import MainNav from './components/MainNav';
import Ticker from './components/Ticker';
import MobileDrawer from './components/MobileDrawer';
import MobileBottomNav from './components/MobileBottomNav';
import MobileSearchBar from './components/MobileSearchBar';
import SiteFooter from './components/SiteFooter';

// Pages
import HomePage from './pages/HomePage';
import FatwaBankPage from './pages/FatwaBankPage';
import SingleFatwaPage from './pages/SingleFatwaPage';
import { ScholarsPage, SingleScholarPage } from './pages/ScholarsPage';
import { ArticlesPage, SingleArticlePage } from './pages/ArticlesPage';
import { LecturesPage, SingleLecturePage, ResearchPage } from './pages/LecturesPage';
import { CausesPage, YouthPage, AboutPage, ContactPage, DonatePage } from './pages/OtherPages';
import SingleResearchPage from './pages/SingleResearchPage';

// ─── Global data context ──────────────────────────────────────────────────
const GlobalDataContext = createContext(null);
export function useGlobalData() { return useContext(GlobalDataContext); }

/** Apply the correct language version to a lecture, falling back to Arabic */
function applyLectureVersion(lecture, lang) {
  const translations = lecture.translations || {};
  const v = translations[lang] || translations['ar'] || {};
  return {
    ...lecture,
    title:         v.title         || lecture.title,
    description:   v.description   || lecture.description,
    video_url:     v.video_url     || lecture.video_url,
    youtube_url:   v.youtube_url   || lecture.youtube_url,
    youtube_embed: v.youtube_embed || lecture.youtube_embed,
  };
}

function GlobalDataProvider({ children }) {
  const { lang } = useLang();

  const { data: scholarsRaw }  = useCachedFetch(`${RESEARCHERS_API}?page=1&language=${lang}`);
  const { data: categoriesRaw} = useCachedFetch(`${CATEGORIES_API}?page=1&language=${lang}`);
  const { data: lecturesRaw }  = useCachedFetch(`${LECTURES_API}?page=1`);

  const scholars   = sortScholars((Array.isArray(scholarsRaw)  ? scholarsRaw  : (scholarsRaw?.data  || [])).filter(s => s.status !== 0));
  const categories = (Array.isArray(categoriesRaw)? categoriesRaw: (categoriesRaw?.data|| [])).filter(c => c.name_ar && c.section === 'fatwas');
  const lectures = (Array.isArray(lecturesRaw) ? lecturesRaw : (lecturesRaw?.data || []))
    .filter(l => lang === 'ar' || (l.translations && l.translations[lang]?.title))
    .map(l => applyLectureVersion(l, lang));

  useEffect(() => {
    prefetch([
      `${FATWA_API}?page=1`,
      `${ARTICLE_API}?page=1&language=${lang}`,
    ]);
  }, [lang]);

  return (
    <GlobalDataContext.Provider value={{ scholars, categories, lectures, lang }}>
      {children}
    </GlobalDataContext.Provider>
  );
}

// ─── Loading screen ───────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem', color: 'var(--ink3)' }}>
      <div style={{ width: 48, height: 48, border: '4px solid var(--gp)', borderTopColor: 'var(--g1)', borderRadius: '50%', animation: 'lpdf-spin .75s linear infinite' }} />
      <p style={{ fontFamily: 'var(--fam-display)', fontSize: 'var(--t-lg)' }}>جارٍ التحميل…</p>
    </div>
  );
}

// ─── Detail route wrappers (fetch by :id) ─────────────────────────────────
function FatwaDetailRoute() {
  const { id } = useParams();
  const { scholars, categories, lang } = useGlobalData();
  const [fatwa, setFatwa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prevFatwa, setPrevFatwa] = useState(null);
  const [nextFatwa, setNextFatwa] = useState(null);

  useEffect(() => {
    setLoading(true);
    setFatwa(null);
    setPrevFatwa(null);
    setNextFatwa(null);
    cachedFetch(`${FATWA_API}/${id}`)
      .then(json => { setFatwa(json.data || json); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!fatwa) return;
    cachedFetch(`${FATWA_API}?page=1&language=${lang}&per_page=500`)
      .then(json => {
        const list = Array.isArray(json) ? json : (json.data || []);
        const idx = list.findIndex(a => String(a.id) === String(fatwa.id));
        setPrevFatwa(idx > 0 ? list[idx - 1] : null);
        setNextFatwa(idx !== -1 && idx < list.length - 1 ? list[idx + 1] : null);
      })
      .catch(() => {});
  }, [fatwa, lang]);

  if (loading) return <LoadingScreen />;
  if (!fatwa) return <Navigate to="/fatwas" replace />;
  return <SingleFatwaPage fatwa={fatwa} scholars={scholars} categories={categories} prevFatwa={prevFatwa} nextFatwa={nextFatwa} />;
}

function ArticleDetailRoute() {
  const { id } = useParams();
  const { scholars, lang } = useGlobalData();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prevArticle, setPrevArticle] = useState(null);
  const [nextArticle, setNextArticle] = useState(null);

  useEffect(() => {
    setLoading(true);
    setArticle(null);
    setPrevArticle(null);
    setNextArticle(null);
    cachedFetch(`${ARTICLE_API}/${id}`)
      .then(json => { setArticle(json.data || json); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!article) return;
    cachedFetch(`${ARTICLE_API}?page=1&language=${lang}&per_page=500`)
      .then(json => {
        const list = Array.isArray(json) ? json : (json.data || []);
        const idx = list.findIndex(a => String(a.id) === String(article.id));
        setPrevArticle(idx > 0 ? list[idx - 1] : null);
        setNextArticle(idx !== -1 && idx < list.length - 1 ? list[idx + 1] : null);
      })
      .catch(() => {});
  }, [article, lang]);

  if (loading) return <LoadingScreen />;
  if (!article) return <Navigate to="/articles" replace />;
  return <SingleArticlePage article={article} scholars={scholars} prevArticle={prevArticle} nextArticle={nextArticle} />;
}

function ScholarDetailRoute() {
  const { id } = useParams();
  const { scholars, categories, lang } = useGlobalData();
  const [scholar, setScholar] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const found = scholars.find(s => String(s.id) === String(id));
    if (found) { setScholar(found); setLoading(false); return; }
    cachedFetch(`${RESEARCHERS_API}/${id}`)
      .then(json => { setScholar(json.data || json); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id, scholars]);

  if (loading) return <LoadingScreen />;
  if (!scholar) return <Navigate to="/scholars" replace />;
  return <SingleScholarPage scholar={scholar} scholars={scholars} categories={categories} lang={lang} />;
}

function LectureDetailRoute() {
  const { id } = useParams();
  const { lectures } = useGlobalData();
  const [lecture, setLecture] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    cachedFetch(`${LECTURES_API}/${id}`)
      .then(json => { setLecture(json.data || json); setLoading(false); })
      .catch(() => {
        const found = lectures.find(l => String(l.id) === String(id));
        setLecture(found || null);
        setLoading(false);
      });
  }, [id]);

  if (loading) return <LoadingScreen />;
  if (!lecture) return <Navigate to="/lectures" replace />;
  return <SingleLecturePage lecture={lecture} lectures={lectures} />;
}

function ResearchDetailRoute() {
  const { id } = useParams();
  const { lang } = useGlobalData();
  const [paper, setPaper] = useState(null);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setPaper(null);
    // The backend has no single-research endpoint (it 404s), so derive the
    // paper from the list — same list-fallback approach used for lectures.
    cachedFetch(`${RESEARCH_API}?page=1&language=${lang}&per_page=500`)
      .then(json => {
        const list = Array.isArray(json) ? json : (json?.data || []);
        setPapers(list);
        setPaper(list.find(p => String(p.id) === String(id)) || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id, lang]);

  if (loading) return <LoadingScreen />;
  if (!paper) return <Navigate to="/research" replace />;
  return <SingleResearchPage paper={paper} papers={papers} />;
}

// ─── Simple route wrappers ────────────────────────────────────────────────
function HomeRoute()     { const d = useGlobalData(); return <HomePage    scholars={d.scholars} categories={d.categories} lang={d.lang} />; }
function FatwasRoute()   { const d = useGlobalData(); return <FatwaBankPage scholars={d.scholars} categories={d.categories} lang={d.lang} />; }
function ScholarsRoute() { const d = useGlobalData(); return <ScholarsPage scholars={d.scholars} />; }
function LecturesRoute() { const d = useGlobalData(); return <LecturesPage lang={d.lang} />; }
function ArticlesRoute() { const d = useGlobalData(); return <ArticlesPage lang={d.lang} />; }
function ResearchRoute() { const d = useGlobalData(); return <ResearchPage lang={d.lang} />; }
function AboutRoute()    { const d = useGlobalData(); return <AboutPage    scholars={d.scholars} categories={d.categories} />; }

// ─── Shell (header + nav + footer) ───────────────────────────────────────
function Shell({ children }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { scholars, categories } = useGlobalData();
  const location = useLocation();

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [location.pathname]);

  return (
    <div>
      <Topbar />
      <SiteHeader onToggleDrawer={() => setDrawerOpen(d => !d)} />
      <MobileSearchBar />
      <MainNav scholars={scholars} categories={categories} />
      <Ticker />
      <MobileDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <main>{children}</main>
      <SiteFooter categories={categories} />
      <MobileBottomNav />
    </div>
  );
}

// ─── All routes ───────────────────────────────────────────────────────────
function AppRoutes() {
  return (
    <Shell>
      <Routes>
        <Route path="/"             element={<HomeRoute />} />
        <Route path="/fatwas"       element={<FatwasRoute />} />
        <Route path="/fatwas/:id"   element={<FatwaDetailRoute />} />
        <Route path="/articles"     element={<ArticlesRoute />} />
        <Route path="/articles/:id" element={<ArticleDetailRoute />} />
        <Route path="/scholars"     element={<ScholarsRoute />} />
        <Route path="/scholars/:id" element={<ScholarDetailRoute />} />
        <Route path="/lectures"     element={<LecturesRoute />} />
        <Route path="/lectures/:id" element={<LectureDetailRoute />} />
        <Route path="/research"     element={<ResearchRoute />} />
        <Route path="/research/:id" element={<ResearchDetailRoute />} />
        <Route path="/causes"       element={<CausesPage />} />
        <Route path="/youth"        element={<YouthPage />} />
        <Route path="/about"        element={<AboutRoute />} />
        <Route path="/contact"      element={<ContactPage />} />
        <Route path="/donate"       element={<DonatePage />} />
        <Route path="*"             element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <GlobalDataProvider>
          <AppRoutes />
        </GlobalDataProvider>
      </LangProvider>
    </BrowserRouter>
  );
}